import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/repository/supabase/schema'

/**
 * v0.8.50 — Deterministic active-workspace selection.
 *
 * Pre-v0.8.50 both resolution paths ran
 * `from('workspaces').select('id').limit(1)` with no ordering. Fine while
 * every user could only see their own single workspace, but once household
 * sharing lands (docs/product/household-sharing.md) an invited spouse is a
 * member of two workspaces — her dormant auto-created Personal one and the
 * shared household — and an unordered `limit(1)` would pick either one at
 * random per sign-in.
 *
 * Rule: among the workspaces the user belongs to, prefer the one with the
 * most members (the household beats a solo Personal workspace), then the
 * earliest joined, then the lowest id so the result never flaps.
 *
 * Works before AND after the 20261031_003 migration: pre-migration the
 * v0.8.0 select-own policy only returns the user's own membership rows, so
 * every count is 1 and the tie-breaks decide.
 */

export interface MembershipRow {
  workspace_id: string
  user_id: string
  invited_at: string
  joined_at: string | null
}

export function pickActiveWorkspaceId(userId: string, rows: MembershipRow[]): string | null {
  const mine = rows.filter((r) => r.user_id === userId)
  if (mine.length === 0) return null

  const memberCounts = new Map<string, number>()
  for (const r of rows) {
    memberCounts.set(r.workspace_id, (memberCounts.get(r.workspace_id) ?? 0) + 1)
  }

  const sorted = [...mine].sort((a, b) => {
    const countDiff =
      (memberCounts.get(b.workspace_id) ?? 0) - (memberCounts.get(a.workspace_id) ?? 0)
    if (countDiff !== 0) return countDiff
    const aJoined = a.joined_at ?? a.invited_at
    const bJoined = b.joined_at ?? b.invited_at
    if (aJoined !== bJoined) return aJoined < bJoined ? -1 : 1
    return a.workspace_id < b.workspace_id ? -1 : a.workspace_id > b.workspace_id ? 1 : 0
  })
  return sorted[0]?.workspace_id ?? null
}

export interface WorkspaceQueryResult {
  id: string | null
  error: string | null
}

/**
 * One resolution attempt. Callers own the retry/backoff/timeout schedule
 * (AuthProvider + resolveAndRebind). Falls back to the legacy
 * `workspaces` read when the user has no membership row at all — every
 * workspace created by `handle_new_user` has one, but this keeps a
 * hand-repaired cloud project from locking its user out.
 */
export async function queryActiveWorkspaceId(
  client: SupabaseClient<Database>,
  userId: string
): Promise<WorkspaceQueryResult> {
  const { data, error } = await client
    .from('workspace_members')
    .select('workspace_id, user_id, invited_at, joined_at')
  if (error) return { id: null, error: error.message }

  const picked = pickActiveWorkspaceId(userId, data ?? [])
  if (picked) return { id: picked, error: null }

  const legacy = await client.from('workspaces').select('id').limit(1).maybeSingle()
  if (legacy.error) return { id: null, error: legacy.error.message }
  return legacy.data?.id
    ? { id: legacy.data.id, error: null }
    : { id: null, error: 'workspace row not found' }
}
